/**
 * CRM Kanban board — the Lead Inbox's Board view. One column per lifecycle
 * stage (funnel order); each column independently pages through
 * GET /api/crm/contacts?lifecycle=<stage> using the inbox's current filters
 * (passed down as props) with a per-column Load more (offset tracked as rows
 * actually received from the server, so drag moves don't skew paging). Archived
 * leads are hidden by default (recruiters archive once a lead is scheduled) but
 * the inbox's "Show archived" toggle folds them back into the columns. Cards
 * open the contact
 * detail; on desktop they can be dragged between columns, which optimistically
 * moves the card (and adjusts the two column counts) then PATCHes
 * lifecycle_status — reverting and surfacing an error on failure. A board-level
 * monotonic request sequence guards every fetch: a filter change bumps the
 * sequence so stale responses (including in-flight Load mores) are dropped.
 * Touch devices can't HTML5-drag — cards still open the detail page where the
 * status can be changed; a hint line below the board covers both paths.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Archive, Loader2 } from 'lucide-react';
import api from '../../services/api';
import { ContactListItem, LifecycleStats, MilestoneKey, contactName } from '../types';
import { LIFECYCLES, FINAL_DISPOSITIONS, STUDY_ATTRIBUTED_STAGES, initials, needsReply, relTime, useTeam } from '../lib';
import { ChannelIcon, StudyChips } from './shared';
import MilestoneChip from './MilestoneChip';
import DispositionReasonModal from './DispositionReasonModal';
import StageStudyConfirmModal from './StageStudyConfirmModal';

const COL_LIMIT = 25;

/**
 * Per-column the milestone whose key just restates that lifecycle column — on
 * the board it would duplicate the column header, so it's suppressed (the
 * cross-cutting milestones like needs_reply / call_scheduled still show). The
 * mixed-lifecycle list/conversation views don't need this.
 */
const COLUMN_MILESTONE: Record<string, MilestoneKey> = {
  scheduled: 'appt_scheduled',
  qualified_pending_records: 'awaiting_records',
  ready_to_schedule: 'ready_to_schedule',
};

/** The inbox's buildParams-relevant filter state (everything except lifecycle). */
export interface KanbanFilters {
  q: string;
  eligibility: string;
  milestone: string;
  study: string;
  owner: string;
  unassigned: boolean;
  /** Include archived leads in the columns (recruiters archive after scheduling). */
  showArchived: boolean;
  sort: string;
  answerQuestion: string;
  answerOp: string;
  answerValue: string;
}

interface KanbanBoardProps {
  filters: KanbanFilters;
  stats: LifecycleStats | null;
  /** Bump to force an immediate refetch of all columns (header Refresh). */
  refreshToken?: number;
  /** Called after a successful drag-move so the parent can refresh stats. */
  onMoved?: () => void;
}

interface ColState {
  rows: ContactListItem[];
  /**
   * Rows actually received from the server (raw batch lengths, before dedup).
   * Used as the Load more offset — rows.length drifts when drags add/remove
   * rows client-side, which would skip or re-fetch server rows.
   */
  fetchedCount: number;
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  error: string | null;
}

const errMsg = (e: any): string =>
  e?.response?.data?.detail || e?.response?.data?.error || e?.message || 'Request failed';

/** Query params shared by every column (same semantics as the list view). */
function baseParams(f: KanbanFilters): Record<string, string> {
  // The pipeline board is a working view — archived leads drop out of it unless
  // the recruiter flips "Show archived" on (leads are commonly archived once
  // scheduled, so hiding them makes late-stage columns look near-empty).
  const p: Record<string, string> = {};
  if (!f.showArchived) p.hide_archived = 'true';
  if (f.q.trim()) p.q = f.q.trim();
  if (f.eligibility.trim()) p.eligibility = f.eligibility.trim();
  if (f.milestone.trim()) p.milestone = f.milestone.trim();
  if (f.study.trim()) p.study = f.study.trim();
  if (f.owner.trim()) p.owner = f.owner.trim();
  if (f.unassigned) p.unassigned = 'true';
  if (f.sort) p.sort = f.sort;
  // Same both-halves guard as the list: the answer filter is only sent once
  // question AND value are present (a half-typed filter would 400 / match nothing).
  if (f.answerQuestion.trim() && f.answerValue.trim()) {
    p.answer_question = f.answerQuestion.trim();
    p.answer_op = f.answerOp;
    p.answer_value = f.answerValue.trim();
  }
  return p;
}

const emptyCols = (loading: boolean): Record<string, ColState> => {
  const out: Record<string, ColState> = {};
  for (const l of LIFECYCLES) {
    out[l.value] = { rows: [], fetchedCount: 0, loading, loadingMore: false, hasMore: false, error: null };
  }
  return out;
};

export default function KanbanBoard({ filters, stats, refreshToken = 0, onMoved }: KanbanBoardProps) {
  const navigate = useNavigate();
  const { byId } = useTeam();

  const [cols, setCols] = useState<Record<string, ColState>>(() => emptyCols(true));
  // Optimistic per-stage count deltas from drag moves, layered over
  // stats.by_status until the parent delivers refreshed stats.
  const [countAdj, setCountAdj] = useState<Record<string, number>>({});
  const [moveError, setMoveError] = useState<string | null>(null);

  // Drag state: the card being dragged and the column currently hovered.
  const [drag, setDrag] = useState<{ id: string; from: string } | null>(null);
  const [dragOver, setDragOver] = useState<string | null>(null);
  // A drop onto a negative-disposition column waits here while the recruiter
  // supplies the required reason (the PATCH 400s without it).
  const [pendingDrop, setPendingDrop] = useState<{ d: { id: string; from: string }; to: string; defaultStudyId: string | null } | null>(null);
  // A drop onto a study-attributed positive column (Screened / Randomized)
  // waits here while the recruiter confirms WHICH study the outcome belongs to
  // (the PATCH 400s without it — pivoted leads must land in the right bucket).
  const [pendingStudyDrop, setPendingStudyDrop] = useState<{ d: { id: string; from: string }; to: string; defaultStudyId: string | null } | null>(null);

  // Latest filters for fetches kicked off from callbacks/timeouts.
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  // Board-level monotonic sequence: bumped on every full refetch so any
  // in-flight column response (initial or Load more) from an older filter
  // set is dropped instead of committed.
  const seqRef = useRef(0);

  const fetchColumn = useCallback(async (lifecycle: string, offset: number, seq: number, append: boolean) => {
    setCols((prev) => ({
      ...prev,
      [lifecycle]: { ...prev[lifecycle], loading: !append, loadingMore: append, error: null },
    }));
    try {
      const res = await api.get('/api/crm/contacts', {
        params: { ...baseParams(filtersRef.current), lifecycle, limit: COL_LIMIT, offset },
      });
      if (seq !== seqRef.current) return; // stale — a newer filter set owns the board
      const batch: ContactListItem[] = res.data?.contacts || [];
      setCols((prev) => {
        const col = prev[lifecycle];
        let rows: ContactListItem[];
        let fetchedCount: number;
        if (append) {
          // Dedup on append: rows created/moved between pages shift the offset
          // and would repeat page-boundary rows (duplicate React keys).
          const seen = new Set(col.rows.map((r) => r.id));
          rows = [...col.rows, ...batch.filter((b) => !seen.has(b.id))];
          // Count the RAW batch (pre-dedup) — that's what the server paged past.
          fetchedCount = col.fetchedCount + batch.length;
        } else {
          rows = batch;
          fetchedCount = batch.length;
        }
        return {
          ...prev,
          [lifecycle]: {
            rows,
            fetchedCount,
            loading: false,
            loadingMore: false,
            hasMore: batch.length === COL_LIMIT,
            error: null,
          },
        };
      });
    } catch (e: any) {
      if (seq !== seqRef.current) return;
      setCols((prev) => ({
        ...prev,
        [lifecycle]: { ...prev[lifecycle], loading: false, loadingMore: false, error: errMsg(e) },
      }));
    }
  }, []);

  const refetchAll = useCallback(() => {
    const seq = ++seqRef.current;
    setMoveError(null);
    setCountAdj({});
    setCols(emptyCols(true));
    LIFECYCLES.forEach((l) => fetchColumn(l.value, 0, seq, false));
  }, [fetchColumn]);

  // Serialize the effective query params so identical params (e.g. a
  // half-typed answer filter) don't refetch. Text filters arrive per keystroke
  // from the parent, so non-initial filter changes debounce 300ms; a
  // refreshToken bump with unchanged filters refetches immediately.
  const filterKey = useMemo(() => JSON.stringify(baseParams(filters)), [filters]);
  const firstRunRef = useRef(true);
  const lastKeyRef = useRef(filterKey);
  useEffect(() => {
    if (firstRunRef.current) {
      firstRunRef.current = false;
      lastKeyRef.current = filterKey;
      refetchAll();
      return;
    }
    if (lastKeyRef.current !== filterKey) {
      lastKeyRef.current = filterKey;
      const t = setTimeout(refetchAll, 300);
      return () => clearTimeout(t);
    }
    refetchAll();
  }, [filterKey, refreshToken, refetchAll]);

  // Fresh stats from the parent already include any moves we adjusted for.
  useEffect(() => {
    setCountAdj({});
  }, [stats]);

  /** Apply a move (optimistic, then PATCH) — optionally with a disposition reason. */
  const performMove = (d: { id: string; from: string }, to: string, extra: Record<string, string> = {}) => {
    const fromCol = cols[d.from];
    const idx = fromCol ? fromCol.rows.findIndex((r) => r.id === d.id) : -1;
    if (idx < 0) return;
    const card = fromCol.rows[idx];
    const seq = seqRef.current;

    setMoveError(null);
    setCols((prev) => {
      const fromRows = prev[d.from].rows.filter((r) => r.id !== d.id);
      // Dedup-guard the destination: a Load more may already have fetched this
      // card into the target column — skip the optimistic insert if so.
      const toRows = prev[to].rows.some((r) => r.id === d.id)
        ? prev[to].rows
        : [{ ...card, lifecycle_status: to }, ...prev[to].rows];
      return {
        ...prev,
        [d.from]: { ...prev[d.from], rows: fromRows },
        [to]: { ...prev[to], rows: toRows },
      };
    });
    setCountAdj((prev) => ({ ...prev, [d.from]: (prev[d.from] || 0) - 1, [to]: (prev[to] || 0) + 1 }));

    api
      .patch(`/api/crm/contacts/${d.id}`, { lifecycle_status: to, ...extra })
      .then(() => {
        if (seq !== seqRef.current) {
          // The board refetched while the PATCH was in flight and may have
          // read pre-commit data — re-pull the two affected columns.
          fetchColumn(d.from, 0, seqRef.current, false);
          fetchColumn(to, 0, seqRef.current, false);
        }
        onMoved?.();
      })
      .catch((e: any) => {
        if (seq !== seqRef.current) return; // board refetched since — fresh data is already correct
        setCols((prev) => {
          const toRows = prev[to].rows.filter((r) => r.id !== d.id);
          const fromRows = prev[d.from].rows.filter((r) => r.id !== d.id);
          fromRows.splice(Math.min(idx, fromRows.length), 0, card);
          return {
            ...prev,
            [to]: { ...prev[to], rows: toRows },
            [d.from]: { ...prev[d.from], rows: fromRows },
          };
        });
        setCountAdj((prev) => ({ ...prev, [d.from]: (prev[d.from] || 0) + 1, [to]: (prev[to] || 0) - 1 }));
        setMoveError(`Couldn't move ${contactName(card)}: ${errMsg(e)}`);
        // A stats refresh mid-flight may have cleared countAdj before the
        // inverse deltas landed — ask the parent for fresh stats to converge.
        onMoved?.();
      });
  };

  /** Drop a dragged card onto a column. Negative dispositions collect a reason
   *  first; study-attributed stages (Screened / Randomized / Screen Failed)
   *  confirm the study the outcome belongs to (the card stays put until the
   *  modal is confirmed). */
  const onDrop = (to: string) => {
    setDragOver(null);
    const d = drag;
    setDrag(null);
    if (!d || d.from === to) return;
    const card = cols[d.from]?.rows.find((r) => r.id === d.id);
    const defaultStudyId = card?.primary_study_id || null;
    if (FINAL_DISPOSITIONS.includes(to)) {
      setPendingDrop({ d, to, defaultStudyId });
      return;
    }
    if (STUDY_ATTRIBUTED_STAGES.includes(to)) {
      setPendingStudyDrop({ d, to, defaultStudyId });
      return;
    }
    performMove(d, to);
  };

  // Any active filter narrows the server result set, so the global stats
  // counts no longer describe what the columns show (sort doesn't narrow).
  const filtersActive =
    filters.unassigned ||
    Boolean(filters.q.trim()) ||
    Boolean(filters.owner.trim()) ||
    Boolean(filters.study.trim()) ||
    Boolean(filters.eligibility.trim()) ||
    Boolean(filters.milestone.trim()) ||
    Boolean(filters.answerQuestion.trim() && filters.answerValue.trim());

  return (
    <div className="space-y-2">
      {pendingDrop && (
        <DispositionReasonModal
          status={pendingDrop.to}
          askStudy={STUDY_ATTRIBUTED_STAGES.includes(pendingDrop.to)}
          defaultStudyId={pendingDrop.defaultStudyId}
          onCancel={() => setPendingDrop(null)}
          onConfirm={(reasonCode, note, studyId) => {
            const { d, to } = pendingDrop;
            setPendingDrop(null);
            const extra: Record<string, string> = { reason_code: reasonCode };
            if (note) extra.disposition_note = note;
            if (studyId) extra.primary_study_id = studyId;
            performMove(d, to, extra);
          }}
        />
      )}
      {pendingStudyDrop && (
        <StageStudyConfirmModal
          status={pendingStudyDrop.to}
          defaultStudyId={pendingStudyDrop.defaultStudyId}
          onCancel={() => setPendingStudyDrop(null)}
          onConfirm={(studyId) => {
            const { d, to } = pendingStudyDrop;
            setPendingStudyDrop(null);
            performMove(d, to, studyId ? { primary_study_id: studyId } : {});
          }}
        />
      )}
      {moveError && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
          {moveError}
        </div>
      )}

      <div className="flex items-start gap-3 overflow-x-auto pb-2">
        {LIFECYCLES.map((l) => {
          const col = cols[l.value];
          // Filtered boards show the column's loaded count ("25+" while more
          // pages remain) — the global stats counts would lie under filters.
          const badge = filtersActive
            ? col.loading
              ? null
              : `${col.rows.length.toLocaleString()}${col.hasMore ? '+' : ''}`
            : stats
              ? Math.max(0, (stats.by_status?.[l.value] ?? 0) + (countAdj[l.value] || 0)).toLocaleString()
              : null;
          const highlighted = drag !== null && dragOver === l.value;
          return (
            <div
              key={l.value}
              onDragOver={(e) => {
                if (!drag) return;
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
                if (dragOver !== l.value) setDragOver(l.value);
              }}
              onDragLeave={(e) => {
                // Ignore leave events into the column's own children.
                if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
                  setDragOver((cur) => (cur === l.value ? null : cur));
                }
              }}
              onDrop={(e) => {
                e.preventDefault();
                onDrop(l.value);
              }}
              className={`w-[85vw] max-w-[18rem] flex-shrink-0 rounded-xl border bg-gray-50 dark:bg-gray-900/40 lg:w-72 ${
                highlighted ? 'border-primary-400 ring-2 ring-primary-400' : 'border-gray-200 dark:border-gray-700'
              }`}
            >
              <div className="flex items-center gap-2 px-3 py-2.5">
                <span className={`h-2 w-2 flex-shrink-0 rounded-full ${l.dot}`} />
                <span className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">{l.label}</span>
                {badge !== null && (
                  <span className="ml-auto flex-shrink-0 rounded-full bg-gray-200 px-2 py-0.5 text-xs text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                    {badge}
                  </span>
                )}
              </div>

              <div className="min-h-[6rem] space-y-2 overflow-y-visible px-2 pb-2 lg:max-h-[65vh] lg:overflow-y-auto">
                {col.error && (
                  <div className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
                    {col.error}
                    <button
                      onClick={() => fetchColumn(l.value, 0, seqRef.current, false)}
                      className="ml-2 font-medium underline transition-colors duration-150 hover:text-red-900 focus:outline-none focus:ring-2 focus:ring-red-500 dark:hover:text-red-300"
                    >
                      Retry
                    </button>
                  </div>
                )}
                {col.loading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-4 w-4 animate-spin text-primary-600" />
                  </div>
                ) : col.rows.length === 0 ? (
                  !col.error && <div className="py-8 text-center text-sm text-gray-400 dark:text-gray-500">No leads</div>
                ) : (
                  col.rows.map((c) => {
                    const reply = needsReply(c.last_comm);
                    const owner = c.owner_user_id ? byId.get(c.owner_user_id) : undefined;
                    return (
                      <div
                        key={c.id}
                        role="button"
                        tabIndex={0}
                        draggable
                        onDragStart={(e) => {
                          e.dataTransfer.setData('text/plain', c.id);
                          e.dataTransfer.effectAllowed = 'move';
                          setDrag({ id: c.id, from: l.value });
                        }}
                        onDragEnd={() => {
                          setDrag(null);
                          setDragOver(null);
                        }}
                        onClick={() => navigate(`/crm/contacts/${c.id}`)}
                        onKeyDown={(e) => {
                          if (e.target !== e.currentTarget) return;
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            navigate(`/crm/contacts/${c.id}`);
                          }
                        }}
                        className={`cursor-pointer rounded-lg border border-gray-200 bg-white p-3 shadow-sm transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700/50 ${
                          drag?.id === c.id ? 'opacity-50' : ''
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <span
                            title={contactName(c)}
                            className={`min-w-0 truncate text-sm text-gray-900 dark:text-gray-100 ${reply ? 'font-bold' : 'font-medium'}`}
                          >
                            {contactName(c)}
                          </span>
                          {c.archived_at && (
                            <span className="flex-shrink-0" title="Conversation archived">
                              <Archive className="h-3 w-3 text-gray-400" />
                            </span>
                          )}
                          {reply && <span className="h-2 w-2 flex-shrink-0 rounded-full bg-primary-500" title="Needs your reply" />}
                        </div>
                        {(c.studies || []).length > 0 && (
                          <div className="mt-1.5">
                            <StudyChips studies={c.studies} max={1} />
                          </div>
                        )}
                        {c.milestone && (
                          <div className="mt-1.5">
                            <MilestoneChip
                              milestone={c.milestone}
                              hideKeys={COLUMN_MILESTONE[l.value] ? [COLUMN_MILESTONE[l.value]] : undefined}
                            />
                          </div>
                        )}
                        <div className="mt-2 flex items-center justify-between gap-2">
                          <span className="flex min-w-0 items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                            {c.last_comm ? (
                              <>
                                <ChannelIcon channel={c.last_comm.channel} />
                                {relTime(c.last_comm.occurred_at)}
                              </>
                            ) : (
                              relTime(c.created_at)
                            )}
                          </span>
                          {c.owner_user_id && (
                            <span
                              title={owner?.name || 'Assigned'}
                              className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-primary-100 text-[10px] font-medium text-primary-700 dark:bg-primary-900/40 dark:text-primary-300"
                            >
                              {initials(owner?.name)}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
                {!col.loading && col.hasMore && (
                  <button
                    onClick={() => fetchColumn(l.value, col.fetchedCount, seqRef.current, true)}
                    disabled={col.loadingMore}
                    className="flex min-h-[44px] w-full items-center justify-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-600 transition-colors duration-150 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 sm:min-h-0"
                  >
                    {col.loadingMore ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                    {col.loadingMore ? 'Loading…' : 'Load more'}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <p className="text-xs text-gray-400 dark:text-gray-500">
        Drag cards between stages, or open a lead to change its status.
      </p>
    </div>
  );
}
